import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api, formatError } from "@/lib/api";
import PasswordField from "@/components/PasswordField";
import PasswordStrength from "@/components/PasswordStrength";
import { toast } from "sonner";
import { ShieldCheck } from "@/lib/icons";
import { useSeo } from "@/lib/seo";

/**
 * Rendered after a temp-password login. Blocks access until the member
 * chooses a new password.
 */
export default function ForceChangePassword() {
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useSeo({ title: "Set a New Password | PRONET", noindex: true });

  if (user === null) return <div className="container-tight py-20 text-white/50">Loading…</div>;
  if (!user || user.role !== "member") return <Navigate to="/login" replace />;
  if (!user.must_change_password) return <Navigate to="/account" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw.length < 8) { setErr("Password must be at least 8 characters"); return; }
    if (pw !== confirm) { setErr("Passwords do not match"); return; }
    setBusy(true);
    try {
      await api.post("/member/password/first-login", { new_password: pw });
      setUser({ ...user, must_change_password: false });
      toast.success("Password updated. You're all set.");
      nav("/account", { replace: true });
    } catch (e) { setErr(formatError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="relative container-tight py-12 md:py-20 max-w-md">
      <div className="blobs-layer">
        <div className="blob blob-violet w-[400px] h-[400px] -top-20 -left-20 opacity-45" />
        <div className="blob blob-magenta w-[380px] h-[380px] bottom-0 right-0 opacity-35" style={{ animationDelay: "-9s" }} />
      </div>
      <form onSubmit={submit} className="relative glass rounded-3xl p-6 sm:p-10 fade-up-slow">
        <div className="overline text-fuchsia-300">Set your password</div>
        <h1 className="mt-3 font-display text-3xl sm:text-4xl leading-none text-white">
          Choose a <span className="italic text-gradient">new password</span>
        </h1>
        <p className="mt-3 text-white/60 text-sm">
          Your account was created by our admin team. Please pick a new password to continue.
          Hi {user.full_name}!
        </p>

        <div className="mt-8 space-y-4">
          <PwInput label="New password" value={pw} onChange={setPw} testid="fcp-new" showStrength />
          <PwInput label="Confirm new password" value={confirm} onChange={setConfirm} testid="fcp-confirm" />
        </div>

        {err && <div className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300" data-testid="fcp-error">{err}</div>}

        <button type="submit" disabled={busy} data-testid="fcp-submit"
          className="mt-8 btn-glow w-full h-12 rounded-full text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60">
          <ShieldCheck size={15} /> {busy ? "Saving…" : "Save & continue"}
        </button>
      </form>
    </div>
  );
}

function PwInput({ label, value, onChange, testid, showStrength }) {
  return (
    <div>
      <label className="overline text-white/60 text-[10px]">{label}</label>
      <div className="mt-2">
        <PasswordField value={value} onChange={onChange} testid={testid} autoComplete="new-password" />
      </div>
      {showStrength && <PasswordStrength password={value} testid={`${testid}-strength`} />}
    </div>
  );
}
